import {ReactElement} from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  Tooltip,
  Typography
} from '@mui/material';
import {TFunction} from 'react-i18next';
import {ApiMutationStatus} from 'interfaces/ApiRequestStatus';
import {Project} from 'interfaces/Project';
import useReportAvailability from 'views/Calculator/components/ProjectConfigurator/hooks/useReportAvailability';

type Props = {
  project: Project;
  t: TFunction;
  onGeneratePdf: () => void;
  requestPdfStatus: ApiMutationStatus;
  onClone: () => void;
  cloneProjectRequestStatus: ApiMutationStatus;
  onDelete: () => void;
  deleteProjectRequestStatus: ApiMutationStatus;
  onSave: () => void;
  saveProjectRequestStatus: ApiMutationStatus;
};

export default function ProjectHeaderView({
  project,
  t,
  onGeneratePdf,
  requestPdfStatus,
  onClone,
  cloneProjectRequestStatus,
  onDelete,
  deleteProjectRequestStatus,
  onSave,
  saveProjectRequestStatus
}: Props): ReactElement {
  const someRequestIsSending =
    requestPdfStatus.isLoading ||
    saveProjectRequestStatus.isLoading ||
    deleteProjectRequestStatus.isLoading;

  const {canGenerate, disabledReason} = useReportAvailability(project);

  return (
    <Box sx={{width: '100%'}}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: {xs: 'column', sm: 'row'},
          alignItems: {xs: 'stretch', sm: 'flex-start'},
          gap: {xs: 1.5, sm: 1}
        }}
      >
        <Box sx={{flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0}}>
          <Typography
            component="h1"
            sx={{
              fontSize: {xs: 26, sm: '2.125rem'},
              lineHeight: 1.235,
              letterSpacing: '0.25px',
              fontWeight: 400,
              color: 'ctaBlack'
            }}
          >
            {project.name}
          </Typography>
          {project.description && <Typography sx={{color: 'ctaBlack'}}>{project.description}</Typography>}
        </Box>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0}}>
          <Tooltip title={t('common:cloneCurrentProject')}>
            <span>
              <IconButton
                aria-label="clone"
                onClick={onClone}
                disabled={someRequestIsSending}
                sx={{color: 'ctaBlack'}}
              >
                <ContentCopyIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canGenerate ? t('common:generatePdf') : disabledReason}>
            <span>
              <IconButton
                aria-label="pdf"
                onClick={onGeneratePdf}
                disabled={someRequestIsSending || !canGenerate}
                sx={{color: 'ctaBlack'}}
              >
                <PictureAsPdfIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('common:delete')}>
            <span>
              <IconButton
                aria-label="delete"
                onClick={onDelete}
                disabled={someRequestIsSending}
                sx={{color: 'ctaBlack'}}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={onSave}
            disabled={someRequestIsSending}
            startIcon={
              saveProjectRequestStatus.isLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
          >
            {t('common:save')}
          </Button>
        </Box>
      </Box>
      {requestPdfStatus.isError && (
        <Alert severity="error" sx={{width: '100%', mt: 2}}>
          {t('common:generatePdfError')}
        </Alert>
      )}
      {deleteProjectRequestStatus.isError && (
        <Alert severity="error" sx={{width: '100%', mt: 2}}>
          {t('common:deleteProject.error')}
        </Alert>
      )}
      <Snackbar
        open={cloneProjectRequestStatus.isLoading}
        autoHideDuration={6000}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
      >
        <Alert severity="info" sx={{width: '100%'}}>
          {t('common:creatingProject')}
        </Alert>
      </Snackbar>
    </Box>
  );
}
